// @flow
import * as React from 'react';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {NewCardPaymentDemo} from 'docs/components';
import {Text} from 'sail/Text';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import CheckoutPreviewImage from 'docs/images/components/demo/checkout-preview.png';

import './ComplexityCallout.scss';

const messages = defineMessages({
  complexity: {
    id: 'docsShell.complexityCallout.label',
    description:
      'A label displayed before a series of dots representing how complex a guide is to integrate (e.g. 1 dot is easy, 5 dots is very hard)',
    defaultMessage: 'Complexity:',
  },
  complexityAriaLabel: {
    id: 'docsShell.complexityCallout.ariaLabel',
    description:
      'A label shown to screen readers that explains how complex a guide is to integrate (e.g. 1 dot is easy, 5 dots is very hard)',
    defaultMessage: `This integration has a complexity score of {complexity} out of 5. A score of 1 means it's easy to integrate while a score of 5 means it's difficult.`,
  },
});

type ComplexityCalloutProps = {|
  complexity: number,
  type: 'elements' | 'checkout',
  image?: string,
  color?: string,
  children: React.Node,
|};

type CircleProps = {|
  color: string,
|};

const Circle = ({color}: CircleProps) => {
  // $FlowExpectedError color wants a specific string but we just have a generic one
  return <Box background={color} className="ComplexityCallout-Circle" />;
};

type CheckoutPreviewProps = {|
  image?: string,
|};

const CheckoutPreview = ({image}: CheckoutPreviewProps) => {
  return (
    <a href="https://checkout.stripe.dev/" target="_blank">
      <img src={image || CheckoutPreviewImage} alt="" />
    </a>
  );
};

export const ComplexityCallout = ({
  complexity,
  type,
  color = 'green',
  image,
  children,
}: ComplexityCalloutProps) => {
  const circles = [...Array(5).keys()];
  const intl = useIntl();
  const ariaLabel = intl.formatMessage(messages.complexityAriaLabel, {
    complexity,
  });

  return (
    <Group
      className="ComplexityCallout"
      direction="horizontal"
      aria-label={ariaLabel}
    >
      {type === 'elements' ? (
        <NewCardPaymentDemo />
      ) : (
        <CheckoutPreview image={image} />
      )}
      <Box
        className="ComplexityCallout-Text"
        flex={{direction: 'column'}}
        style={{marginLeft: '40px'}}
      >
        <Group
          direction="horizontal"
          spacing={8}
          alignItems="center"
          aria-hidden
        >
          <Text color="gray">
            <FormattedMessage {...messages.complexity} />
          </Text>
          {circles.map((i) => (
            <Circle
              key={`circle-${color}-${i}`}
              color={i < complexity ? `${color}500` : 'gray100'}
            />
          ))}
        </Group>
        <Group>{children}</Group>
      </Box>
    </Group>
  );
};
